import { useMemo } from 'react';
import { QuantityPricePair } from '../types';
import { calculateDeliveryInfo } from '../utils/deliveryDateCalculator';
import { useProducts } from './useProducts';

/**
 * Calcula la etiqueta de entrega con el mismo criterio que el Cotizador Express:
 *   "Entrega Hoy"         → fecha de entrega es hoy
 *   "Entrega Mañana"      → fecha de entrega es mañana
 *   "Entrega en X días"   → cualquier otro caso
 *
 * Usa los horarios de corte y configuración real de la tienda (helpSettings).
 * Si no se puede calcular, cae en el fallback del raw deliveryTime o "Consultar".
 */
export const useDeliveryLabel = (priceInfo: QuantityPricePair | null | undefined): string => {
    const { helpSettings } = useProducts();

    return useMemo(() => {
        const fallback = priceInfo?.deliveryTime || 'Consultar';

        if (!priceInfo || !helpSettings) return fallback;

        const deliveryInfo = calculateDeliveryInfo(priceInfo, false, helpSettings);

        if (!deliveryInfo) return fallback;

        const now = new Date();
        const today  = new Date(now.getFullYear(),  now.getMonth(),  now.getDate());
        const target = new Date(
            deliveryInfo.estimatedDeliveryDate.getFullYear(),
            deliveryInfo.estimatedDeliveryDate.getMonth(),
            deliveryInfo.estimatedDeliveryDate.getDate()
        );

        const diffDays = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) return 'Entrega Hoy';
        if (diffDays === 1) return 'Entrega Mañana';
        return `Entrega en ${diffDays} días`;
    }, [priceInfo, helpSettings]);
};
